import { useFormik } from 'formik';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { postAssetYearlyTaxReport, putAssetYearlyTaxReport } from 'src/api/stoApi';
import { CurrencyFieldText } from 'src/components/mui-overrides/Currency';
import { NormalDatepicker } from 'src/components/mui-overrides/Datepicker';
import { DefaultSwitch } from 'src/components/mui-overrides/Switch';
import { DefaultTextField } from 'src/components/mui-overrides/Textfield';
import { IAssetYearlyTaxReports } from 'src/models/index';
import { awaitUtil } from 'src/util/utils';
import { object, string } from 'yup';
import CloseIcon from '@mui/icons-material/Close';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';

interface Props {
  open: boolean;
  handleClose: () => void;
  handleSave: () => void;
  selectedTaxReport: IAssetYearlyTaxReports | null | undefined;
  assetId: string;
}

const validationSchemaTaxReport = object({
  year: string().required('Required'),
  income: string().required('Required'),
  taxOffice: string().required('Required'),
  taxNumber: string().required('Required'),
  taxOfficeTeam: string().required('Required'),
  megName: string().required('Required'),
  taxRepresentative: string().required('Required'),
});

const getInitValuesTaxReport = (taxReport: IAssetYearlyTaxReports | null | undefined) => {
  return {
    year: taxReport?.year || '',
    ...(taxReport?.income.$numberDecimal && { income: taxReport?.income.$numberDecimal }),
    taxOffice: taxReport?.megData?.taxOffice || '',
    taxNumber: taxReport?.megData?.taxNumber || '',
    taxOfficeTeam: taxReport?.megData?.taxOfficeTeam || '',
    megName: taxReport?.megData?.megName || '',
    taxRepresentative: taxReport?.megData?.taxRepresentative || '',
    grossTaxSystem: taxReport?.megData?.grossTaxSystem || false,
    ...(taxReport?.advertisingExpenses?.type9470?.$numberDecimal && {
      type9470: Number.parseFloat(taxReport?.advertisingExpenses?.type9470?.$numberDecimal),
    }),
    ...(taxReport?.advertisingExpenses?.type9480?.$numberDecimal && {
      type9480: Number.parseFloat(taxReport?.advertisingExpenses?.type9480?.$numberDecimal),
    }),
    ...(taxReport?.advertisingExpenses?.type9490?.$numberDecimal && {
      type9490: Number.parseFloat(taxReport?.advertisingExpenses?.type9490?.$numberDecimal),
    }),
    ...(taxReport?.advertisingExpenses?.type9134?.$numberDecimal && {
      type9134: Number.parseFloat(taxReport?.advertisingExpenses?.type9134?.$numberDecimal),
    }),
    ...(taxReport?.advertisingExpenses?.type9135?.$numberDecimal && {
      type9135: Number.parseFloat(taxReport?.advertisingExpenses?.type9135?.$numberDecimal),
    }),
    ...(taxReport?.advertisingExpenses?.type9505?.$numberDecimal && {
      type9505: Number.parseFloat(taxReport?.advertisingExpenses?.type9505?.$numberDecimal),
    }),
    ...(taxReport?.advertisingExpenses?.type9510?.$numberDecimal && {
      type9510: Number.parseFloat(taxReport?.advertisingExpenses?.type9510?.$numberDecimal),
    }),
    ...(taxReport?.advertisingExpenses?.type9520?.$numberDecimal && {
      type9520: Number.parseFloat(taxReport?.advertisingExpenses?.type9520?.$numberDecimal),
    }),
    ...(taxReport?.advertisingExpenses?.type9530?.$numberDecimal && {
      type9530: Number.parseFloat(taxReport?.advertisingExpenses?.type9530?.$numberDecimal),
    }),
    ...(taxReport?.advertisingExpenses?.type9540?.$numberDecimal && {
      type9540: Number.parseFloat(taxReport?.advertisingExpenses?.type9540?.$numberDecimal),
    }),
  };
};

export const AddEditTaxReportModal: FC<Props> = ({ open, handleClose, handleSave, selectedTaxReport, assetId }) => {
  const { t } = useTranslation();
  const [hasErrors, setHasErrors] = useState(false);
  const [sendingData, setSendingData] = useState(false);
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: getInitValuesTaxReport(selectedTaxReport),
    validationSchema: validationSchemaTaxReport,
    validateOnChange: true,
    onSubmit: async (val) => {
      const body = {
        income: val.income,
        megData: {
          taxOffice: val.taxOffice,
          taxNumber: val.taxNumber,
          taxOfficeTeam: val.taxOfficeTeam,
          megName: val.megName,
          taxRepresentative: val.taxRepresentative,
          grossTaxSystem: val.grossTaxSystem,
        },
        advertisingExpenses: {
          type9470: val.type9470,
          type9480: val.type9480,
          type9490: val.type9490,
          type9134: val.type9134,
          type9135: val.type9135,
          type9505: val.type9505,
          type9510: val.type9510,
          type9520: val.type9520,
          type9530: val.type9530,
          type9540: val.type9540,
        },
      };
      const year = new Date(val.year).getFullYear().toString();
      setSendingData(true);
      // PUT / Edit asset if report has assetId
      if (selectedTaxReport?.assetId) {
        const [res, error] = await awaitUtil(putAssetYearlyTaxReport(assetId, year, body));
        if (res) {
          handleSave();
          setSendingData(false);
          resetForm();
        }
        if (error) {
          setSendingData(false);
        }
        // POST / Add asset if report has no assetId
      } else {
        const [res, error] = await awaitUtil(postAssetYearlyTaxReport(assetId, year, body));
        if (res) {
          handleSave();
          setSendingData(false);
          resetForm();
        }
        if (error) {
          setSendingData(false);
        }
      }
    },
  });

  // reset form
  const resetForm = () => {
    formik.resetForm();
    setHasErrors(false);
  };

  const sendForm = async () => {
    let errors = false;
    await formik.validateForm();
    if (Object.keys(formik.errors).length > 0) {
      await formik.setTouched({ ...formik.touched, ...(formik.errors as any) });
    }
    // check if any form has errors
    if (Object.keys(formik.errors).length > 0) {
      errors = true;
      setHasErrors(true);
    }
    setHasErrors(hasErrors);
    if (!errors) {
      await formik.submitForm();
    }
  };
  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="xl">
        <DialogTitle>
          <Grid container>
            <Typography flexGrow={1} variant="h5" sx={{ fontWeight: 500 }}>
              {t('asset.yearlyTaxReport.modal.title')}
            </Typography>
            <IconButton
              onClick={() => {
                handleClose();
                resetForm();
              }}
            >
              <CloseIcon />
            </IconButton>
          </Grid>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>{t('asset.yearlyTaxReport.modal.subTitle')}</DialogContentText>
          <form onSubmit={formik.handleSubmit}>
            <Grid container rowSpacing={0} columnSpacing={3}>
              <Grid xs={6} md={3}>
                <NormalDatepicker ddmmyy={['year']} label={t('asset.yearlyTaxReport.form.year')} fieldName="year" formik={formik} />
              </Grid>
              <Grid xs={6} md={3}>
                <DefaultTextField
                  label={t('asset.yearlyTaxReport.taxOffice')}
                  placeholder={t('asset.yearlyTaxReport.taxOffice.placeholder')}
                  fieldName="taxOffice"
                  formik={formik}
                />
              </Grid>
              <Grid xs={6} md={3}>
                <DefaultTextField
                  label={t('asset.yearlyTaxReport.taxNumber')}
                  placeholder={t('asset.yearlyTaxReport.taxNumber.placeholder')}
                  fieldName="taxNumber"
                  formik={formik}
                />
              </Grid>
              <Grid xs={6} md={3}>
                <DefaultTextField
                  label={t('asset.yearlyTaxReport.taxOfficeTeam')}
                  placeholder={t('asset.yearlyTaxReport.taxOfficeTeam.placeholder')}
                  fieldName="taxOfficeTeam"
                  formik={formik}
                />
              </Grid>
              <Grid xs={6} md={3}>
                <DefaultTextField
                  label={t('asset.yearlyTaxReport.megName')}
                  placeholder={t('asset.yearlyTaxReport.megName.placeholder')}
                  fieldName="megName"
                  formik={formik}
                />
              </Grid>
              <Grid xs={6} md={3}>
                <DefaultTextField
                  label={t('asset.yearlyTaxReport.taxRepresentative')}
                  placeholder={t('asset.yearlyTaxReport.taxRepresentative.placeholder')}
                  fieldName="taxRepresentative"
                  formik={formik}
                />
              </Grid>

              <Grid xs={12} md={6}>
                <Typography variant="body1" marginTop={2} sx={{ fontWeight: 500 }}>
                  {t('asset.yearlyTaxReport.vatSystem')}
                </Typography>
                <DefaultSwitch
                  fieldName="grossTaxSystem"
                  disabled={false}
                  labelRight={t('asset.yearlyTaxReport.vatSystem.gross')}
                  labelLeft={t('asset.yearlyTaxReport.vatSystem.net')}
                  formik={formik}
                />
              </Grid>
            </Grid>
            <Divider />
            <Grid container rowSpacing={0} columnSpacing={3} columns={10}>
              <Grid xs={12}>
                <Typography variant="h5" marginTop={2} sx={{ fontWeight: 500 }}>
                  {t('asset.yearlyTaxReport.income')}
                </Typography>
              </Grid>
              <Grid xs={6} md={3}>
                <CurrencyFieldText currency="€" label={t('asset.yearlyTaxReport.form.revenue')} fieldName="income" formik={formik} />
              </Grid>
            </Grid>
            <Divider />
            <Grid container rowSpacing={0} columnSpacing={3} columns={10}>
              <Grid xs={12}>
                <Typography variant="h5" marginTop={2} sx={{ fontWeight: 500 }}>
                  {t('asset.yearlyTaxReport.AdCosts')}
                </Typography>
              </Grid>
              <Grid xs={4} md={2}>
                <CurrencyFieldText label={t('asset.yearlyTaxReport.type') + 9470} fieldName="type9470" formik={formik} currency="€" />
              </Grid>
              <Grid xs={4} md={2}>
                <CurrencyFieldText label={t('asset.yearlyTaxReport.type') + 9480} fieldName="type9480" formik={formik} currency="€" />
              </Grid>
              <Grid xs={4} md={2}>
                <CurrencyFieldText label={t('asset.yearlyTaxReport.type') + 9490} fieldName="type9490" formik={formik} currency="€" />
              </Grid>
              <Grid xs={4} md={2}>
                <CurrencyFieldText label={t('asset.yearlyTaxReport.type') + 9134} fieldName="type9134" formik={formik} currency="€" />
              </Grid>
              <Grid xs={4} md={2}>
                <CurrencyFieldText label={t('asset.yearlyTaxReport.type') + 9135} fieldName="type9135" formik={formik} currency="€" />
              </Grid>
              <Grid xs={4} md={2}>
                <CurrencyFieldText label={t('asset.yearlyTaxReport.type') + 9505} fieldName="type9505" formik={formik} currency="€" />
              </Grid>
              <Grid xs={4} md={2}>
                <CurrencyFieldText label={t('asset.yearlyTaxReport.type') + 9510} fieldName="type9510" formik={formik} currency="€" />
              </Grid>
              <Grid xs={4} md={2}>
                <CurrencyFieldText label={t('asset.yearlyTaxReport.type') + 9520} fieldName="type9520" formik={formik} currency="€" />
              </Grid>
              <Grid xs={4} md={2}>
                <CurrencyFieldText label={t('asset.yearlyTaxReport.type') + 9530} fieldName="type9530" formik={formik} currency="€" />
              </Grid>
              <Grid xs={4} md={2}>
                <CurrencyFieldText label={t('asset.yearlyTaxReport.type') + 9540} fieldName="type9540" formik={formik} currency="€" />
              </Grid>
            </Grid>
          </form>
          <div>{Object.keys(formik.errors)}</div>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => {
              handleClose();
              resetForm();
            }}
          >
            {t('asset.yearlyTaxReport.modal.cancel')}
          </Button>
          <LoadingButton variant="contained" loading={sendingData} onClick={sendForm} disabled={Object.keys(formik.errors).length !== 0}>
            {t('asset.yearlyTaxReport.modal.save')}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};
